import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { shopApi } from "../lib/shop";

const CartCtx = createContext(null);
const STORAGE_KEY = "rhm_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) { return []; }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    // Save the cart server-side for signed-in shoppers so we can send one gentle reminder later.
    useEffect(() => {
        if (!localStorage.getItem("rhm_customer_token")) return undefined;
        const timer = setTimeout(() => {
            shopApi.post("/cart/snapshot", {
                items: items.map((i) => ({
                    slug: i.product.slug, name: i.product.name,
                    qty: i.quantity, price: i.product.price * i.quantity,
                })),
            }).catch(() => undefined);
        }, 2500);
        return () => clearTimeout(timer);
    }, [items]);

    const addItem = (product, qty = 1) => {
        setItems((prev) => {
            const found = prev.find((i) => i.product.id === product.id);
            if (found) {
                return prev.map((i) =>
                    i.product.id === product.id ? { ...i, quantity: i.quantity + qty } : i
                );
            }
            return [...prev, { product, quantity: qty }];
        });
        setOpen(true);
    };

    const removeItem = (productId) =>
        setItems((prev) => prev.filter((i) => i.product.id !== productId));

    const updateQty = (productId, qty) => {
        if (qty <= 0) return removeItem(productId);
        setItems((prev) =>
            prev.map((i) => (i.product.id === productId ? { ...i, quantity: qty } : i))
        );
    };

    const clear = () => setItems([]);

    const subtotal = useMemo(
        () => items.reduce((s, i) => s + i.product.price * i.quantity, 0),
        [items]
    );
    const count = useMemo(
        () => items.reduce((s, i) => s + i.quantity, 0),
        [items]
    );

    const value = { items, addItem, removeItem, updateQty, clear, subtotal, count, open, setOpen };
    return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartCtx);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};
